import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import { TableSkeleton } from "../../Components/Accounting/SkeletonLoader";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

/**
 * MANUAL BANK RECONCILIATION (tick-off)
 *
 * Uganda has no direct bank feeds, so this workflow is entirely manual:
 * the user loads the bank transactions the system already recorded, then
 * ticks off each one that also appears on the PDF statement the bank sent.
 * Each tick is persisted (acc_journal_lines.reconciled) via bank/reconcile,
 * and can be undone via bank/unreconcile. This is a separate option from the
 * CSV auto-match reconciliation.
 */
const ManualBankReconciliation = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statementBalance, setStatementBalance] = useState("");
  const [bankReference, setBankReference] = useState("");
  const [report, setReport] = useState(null);
  // checked[lineId] = true  → line is (or should be) reconciled
  const [checked, setChecked] = useState({});
  // snapshot of the server's reconciled state, to diff against on save
  const [serverChecked, setServerChecked] = useState({});

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions();
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, fromDate, toDate]);

  const fetchBankAccounts = async () => {
    const res = await ajaxAccounting.listAccounts({ type: "asset" });
    if (res.status === "OK") {
      const bankAccts = (res.details || []).filter(
        (a) =>
          a.account_code?.startsWith("101") ||
          a.account_code?.startsWith("102") ||
          a.sub_type === "bank" ||
          a.name?.toLowerCase().includes("bank") ||
          a.name?.toLowerCase().includes("cash")
      );
      setAccounts(bankAccts);
      if (bankAccts.length > 0) setSelectedAccount(String(bankAccts[0].id));
    }
  };

  // Load every ledger line for the period (loop pages so a full statement
  // fits on one screen for tick-off, regardless of the backend page size).
  const fetchTransactions = async () => {
    setLoading(true);
    const all = [];
    let page = 1;
    let totalPages = 1;
    do {
      const res = await ajaxAccounting.listBankTransactions({
        account_id: parseInt(selectedAccount),
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        page,
      });
      if (res.status !== "OK") break;
      const list = res.details?.list || [];
      all.push(...list);
      totalPages = res.details?.meta?.total_pages || 1;
      page += 1;
    } while (page <= totalPages && page < 100);

    setRows(all);
    const initial = {};
    all.forEach((r) => {
      if (r.reconciled) initial[r.id] = true;
    });
    setChecked(initial);
    setServerChecked(initial);
    setLoading(false);
  };

  const fetchReport = async () => {
    const res = await ajaxAccounting.getReconciliationReport({
      account_id: parseInt(selectedAccount),
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
    });
    if (res.status === "OK") setReport(res.details);
  };

  const toggle = (id) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = (value) => {
    const next = {};
    rows.forEach((r) => {
      next[r.id] = value;
    });
    setChecked(next);
  };

  // ids that changed vs the server snapshot
  const toReconcile = rows
    .filter((r) => checked[r.id] && !serverChecked[r.id])
    .map((r) => r.id);
  const toUnreconcile = rows
    .filter((r) => !checked[r.id] && serverChecked[r.id])
    .map((r) => r.id);
  const dirty = toReconcile.length > 0 || toUnreconcile.length > 0;

  const saveReconciliation = async () => {
    if (!dirty) {
      toast("Nothing changed since the last save.");
      return;
    }
    setSaving(true);
    let ok = true;
    if (toReconcile.length > 0) {
      const res = await ajaxAccounting.reconcileTransaction({
        journal_line_ids: toReconcile,
        bank_reference: bankReference || "",
      });
      if (res.status !== "OK") {
        ok = false;
        toast.error(res.message || "Failed to reconcile some lines.");
      }
    }
    if (ok && toUnreconcile.length > 0) {
      const res = await ajaxAccounting.unreconcileTransaction({
        journal_line_ids: toUnreconcile,
      });
      if (res.status !== "OK") {
        ok = false;
        toast.error(res.message || "Failed to un-reconcile some lines.");
      }
    }
    if (ok) {
      toast.success(
        `Saved: ${toReconcile.length} reconciled, ${toUnreconcile.length} un-reconciled.`
      );
      await fetchTransactions();
      await fetchReport();
    }
    setSaving(false);
  };

  // ─── derived figures ────────────────────────────────────────────────
  // Bank GL balance = debits - credits across the whole period (report).
  const ledgerBalance = report
    ? report.ledger.total_debit - report.ledger.total_credit
    : rows.reduce((s, r) => s + (Number(r.debit) || 0) - (Number(r.credit) || 0), 0);
  // Cleared (reconciled) balance = ticked lines only.
  const clearedBalance = report
    ? report.ledger.reconciled_debit - report.ledger.reconciled_credit
    : 0;
  const stmt = parseFloat(statementBalance) || 0;
  const difference = stmt - clearedBalance;
  const inBalance = Math.abs(difference) < 0.01;
  const reconciledCount = rows.filter((r) => checked[r.id]).length;
  const unreconciledCount = rows.length - reconciledCount;

  const allChecked = rows.length > 0 && rows.every((r) => checked[r.id]);

  const columns = [
    {
      key: "_sel",
      label: (
        <input
          type="checkbox"
          checked={allChecked}
          onChange={(e) => toggleAll(e.target.checked)}
          title="Tick all"
        />
      ),
      render: (_, row) => (
        <input
          type="checkbox"
          checked={!!checked[row.id]}
          onChange={() => toggle(row.id)}
        />
      ),
    },
    { key: "date", label: "Date", render: (v) => functions.formatDate(v) },
    { key: "reference", label: "Ref", render: (v) => <span className="tx-12 tx-muted">{v || "-"}</span> },
    { key: "description", label: "Description", render: (v) => <span className="tx-12">{v || "-"}</span> },
    {
      key: "debit",
      label: "Money In (Dr)",
      render: (v) => (Number(v) > 0 ? <span className="tx-success">{functions.formatCurrency(v)}</span> : "-"),
    },
    {
      key: "credit",
      label: "Money Out (Cr)",
      render: (v) => (Number(v) > 0 ? <span className="tx-danger">{functions.formatCurrency(v)}</span> : "-"),
    },
    {
      key: "_status",
      label: "On Statement",
      render: (_, row) =>
        checked[row.id] ? (
          <span className="badge bg-success"><i className="fa-solid fa-check me-1"></i>Ticked</span>
        ) : (
          <span className="badge bg-light text-dark">Pending</span>
        ),
    },
  ];

  return (
    <AppContainer
      title="Manual Bank Reconciliation"
      previousPageTitle="Accounting"
      description="Tick off each transaction the system recorded against the PDF statement your bank sent. Every tick is saved, so you can reconcile a statement over several sittings. No bank integration required."
      actions={[
        {
          label: saving ? "Saving…" : "Save Reconciliation",
          icon: "fa-solid fa-floppy-disk",
          className: "btn btn-primary",
          onClick: saveReconciliation,
          disabled: saving || !dirty,
        },
      ]}
    >
      <Toaster />

      {/* Filters */}
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Bank / Cash Account</label>
            <Select2
              options={accounts.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }))}
              value={selectedAccount}
              onChange={setSelectedAccount}
              placeholder="Select account"
            />
          </div>
          <div className="col-lg-2">
            <label className="form-label tx-12 fw-semibold">Statement From</label>
            <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="col-lg-2">
            <label className="form-label tx-12 fw-semibold">Statement To</label>
            <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="col-lg-2">
            <label className="form-label tx-12 fw-semibold">Statement Closing Balance</label>
            <input type="number" className="form-control" placeholder="0.00" value={statementBalance} onChange={(e) => setStatementBalance(e.target.value)} />
          </div>
          <div className="col-lg-2">
            <label className="form-label tx-12 fw-semibold">Statement Ref (optional)</label>
            <input type="text" className="form-control" placeholder="e.g. JUN-2026" value={bankReference} onChange={(e) => setBankReference(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="row mb-3">
        {[
          { label: "Book Balance (GL)", value: ledgerBalance, color: "#6366f1" },
          { label: "Reconciled (Cleared)", value: clearedBalance, color: "#0ea5e9" },
          { label: "Statement Balance", value: stmt, color: "#10b981" },
          { label: "Difference", value: difference, color: inBalance ? "#10b981" : "#ef4444" },
          { label: "Ticked Lines", value: reconciledCount, color: "#22c55e", type: "count" },
          { label: "Not Yet Ticked", value: unreconciledCount, color: "#f59e0b", type: "count" },
        ].map((c, i) => (
          <div key={i} className="col-xl-2 col-lg-4 col-sm-6 mb-2">
            <div className="card custom-card p-2" style={{ borderLeft: `3px solid ${c.color}` }}>
              <div className="tx-11 tx-muted mb-1">{c.label}</div>
              <div className="fw-bold" style={{ color: c.color }}>
                {c.type === "count" ? c.value : functions.formatCurrency(c.value)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {stmt > 0 && (
        <div className={`alert ${inBalance ? "alert-success" : "alert-warning"} tx-13 py-2`}>
          {inBalance ? (
            <><i className="fa-solid fa-circle-check me-1"></i> Reconciled — your cleared balance matches the statement.</>
          ) : (
            <><i className="fa-solid fa-triangle-exclamation me-1"></i> Off by {functions.formatCurrency(Math.abs(difference))}. Keep ticking items that appear on the statement (or investigate unmatched ones) until the difference is zero.</>
          )}
        </div>
      )}

      {/* Transactions */}
      <div className="card custom-card p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-semibold mb-0">
            System Bank Transactions <span className="badge bg-secondary ms-1">{rows.length}</span>
          </h6>
          {dirty && (
            <span className="tx-12 tx-warning">
              Unsaved: {toReconcile.length} to tick, {toUnreconcile.length} to untick
            </span>
          )}
        </div>
        {loading ? (
          <TableSkeleton rows={6} cols={7} />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            loading={false}
            pageSize={25}
            emptyMessage="No posted bank transactions for this account/period. Post some journal entries or widen the date range."
          />
        )}
      </div>
    </AppContainer>
  );
};

export default ManualBankReconciliation;
